import React from "react";
import { Helmet } from "react-helmet";
import TechIntro from "../ReusableComponents/TechIntro";
import TechWhyChoose from "../ReusableComponents/TechWhyChoose";
import TechRoles from "../ReusableComponents/TechRoles";
import TechStaffingServices from "../ReusableComponents/TechStaffingServices";
import TechRecruitingAgency from "../ReusableComponents/TechRecruitingAgency";
import TechOutcomes from "../ReusableComponents/TechOutcomes";
import TechCTA from "../ReusableComponents/TechCTA";

const roleGroups = [
  {
    title: 'CloudSuite & Industry Solutions',
    items: [
      { name: 'Infor CloudSuite Industrial', icon: '🏭' },
      { name: 'Infor CloudSuite Healthcare', icon: '🏥' },
      { name: 'Infor CloudSuite Distribution', icon: '📦' },
      { name: 'Infor CloudSuite Fashion', icon: '👗' },
    ]
  },
  {
    title: 'ERP Platforms',
    items: [
      { name: 'Infor LN (Baan)', icon: '⚙️' },
      { name: 'Infor M3', icon: '📊' },
      { name: 'Infor VISUAL', icon: '👁️' },
      { name: 'Infor WFM / HR', icon: '👥' },
    ]
  },
  {
    title: 'Technical & Integration',
    items: [
      { name: 'Infor ION Developer', icon: '🔗' },
      { name: 'Infor Birst / BI', icon: '📈' },
      { name: 'Infor GT Nexus', icon: '🌐' },
      { name: 'Infor Technical Developer', icon: '💻' },
    ]
  }
];

const features = [
  {
    icon: '✦',
    title: 'Infor Ecosystem Expertise',
    text: 'Our recruiters understand the Infor product family in depth — CloudSuite, LN, M3, VISUAL, Birst, ION. We match candidates on specific product version and industry, not just "Infor ERP" as a generic label.',
  },
  {
    icon: '✔',
    title: 'Rigorous Infor-Specific Vetting',
    text: 'All Infor consultants are verified for: specific Infor product and version expertise, hands-on implementation or upgrade experience, industry vertical knowledge (manufacturing, healthcare, distribution, fashion), and Infor ION integration and IDM experience.',
  },
  {
    icon: '⚙',
    title: 'Flexible Engagement Models',
    text: 'Contract, contract-to-hire, or permanent. From a single Infor LN consultant to a complete CloudSuite implementation team — we structure engagements around your project scope and budget.',
  },
  {
    icon: '🛡',
    title: 'Infor Cloud Migration Specialists',
    text: 'We source consultants experienced in on-premise to CloudSuite SaaS migrations — reducing risk on your most complex and high-value Infor transformation programs.',
  },
];

const problemItems = [
  'ERP implementation delays from consultants unfamiliar with Infor\'s industry-specific architecture and workflows.',
  'Configuration errors due to lack of experience with Infor ION, Ming.le, or CloudSuite-specific modules.',
  'Expensive rework from functional consultants who misunderstand your manufacturing or distribution processes.',
  'Infor Cloud migration risk from consultants without CloudSuite or multi-tenant SaaS upgrade expertise.',
  'Underutilized Infor investment when organizations can\'t maximize the platform due to talent gaps.',
];

const solutionPoints = [
  'Infor-specific recruiters who understand the Infor product landscape',
  'Real implementation experience verified before candidate profiles reach your team',
  'Expertise across CloudSuite Automotive, Industrial, Fashion, Food & Beverage, Healthcare, and Distribution',
];

const outcomes = [
  {
    title: 'Successful Infor go-lives',
    text: 'Certified domain experts who deliver implementations on time and within scope.',
  },
  {
    title: 'Industry-fit ERP configuration',
    text: 'Consultants who understand your vertical\'s processes — not just generic ERP flows.',
  },
  {
    title: 'Smooth CloudSuite migrations',
    text: 'Specialists who minimize disruption during on-premise to SaaS transitions.',
  },
  {
    title: 'Maximum Infor ROI',
    text: 'Experts who configure the platform to its full capability, delivering real business value.',
  },
  {
    title: 'Reduced Infor talent risk',
    text: 'Pre-vetted professionals from a rare, specialized talent pool — verified before they reach you.',
  },
];

const InforStaffHome = () => {
  return (
    <div>
      <Helmet>
        <title>Infor Staffing Solutions | Certified CloudSuite & LN Consultants | Kapil Technologies</title>
        <meta
          name="description"
          content="Kapil Technologies provides certified Infor staffing solutions. Hire pre-vetted consultants for CloudSuite, LN, M3, and VISUAL ERP projects."
        />
        <meta
          name="keywords"
          content="Infor staffing, Infor CloudSuite consultants, Infor LN recruitment, Infor M3 staffing, Infor VISUAL experts, Infor ION developers, ERP staffing India, Kapil Technologies Infor"
        />
        <meta name="author" content="Kapil Technologies" />
        <meta name="robots" content="index, follow" />
        <meta
          property="og:title"
          content="Infor Staffing Solutions | Certified CloudSuite & LN Consultants"
        />
        <meta
          property="og:description"
          content="Certified Infor staffing solutions. Hire pre-vetted consultants for CloudSuite, LN, M3, and VISUAL ERP projects."
        />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_IN" />
        <link
          rel="canonical"
          href="https://kapiltechnologies.com/staff-augmentation/infor"
        />
      </Helmet>

      {/* Section 1: Hero */}
      <TechIntro 
        videoUrl="https://res.cloudinary.com/djaejg3nn/video/upload/v1781602780/Kapil_Tech_Infor_Staffing_Page_b19poj.mp4"
        titleWhite="Certified Infor CloudSuite & LN Consultants."
        titleGradient="Industry-Proven Talent."
      />

      {/* Section 2: Why Choose */}
      <TechWhyChoose 
        title="Can't Find Experienced Infor Consultants for Your ERP Project?"
        description="Kapil Tech connects organizations with pre-vetted Infor specialists across Infor CloudSuite, LN, M3, VISUAL, and industry-specific solutions. The Infor talent pool is narrow — our focused practice means you get genuine domain experts, not generic ERP consultants."
        stats={[
          { value: "150+", label: "Infor Experts Placed" },
          { value: "96%", label: "Client Satisfaction Rate" }
        ]}
        imageUrl="https://ik.imagekit.io/7wqj9br0b/KapilTech_Website_Staffing/Technology/Kapil_Tech_Infor_Staffing_Page.jpg?updatedAt=1781603013881"
        imageAlt="Infor Staffing by Kapil Tech"
      />

      {/* Section 3: Roles & Specializations */}
      <TechRoles 
        title="Infor Products & Roles We Staff"
        description="Our Infor talent network covers every major product, module, and technical role in the Infor ecosystem."
        roleGroups={roleGroups}
      />

      {/* Section 4: Why Enterprises Choose Us */}
      <TechStaffingServices 
        title="Why Organizations Choose Kapil Tech for Infor Staffing"
        description="We combine deep Infor domain expertise with an agile staffing model to deliver top-tier talent at speed."
        features={features}
      />

      {/* Section 5: Recruiting Agency / Problem-Solution */}
      <TechRecruitingAgency 
        title="Is the Limited Infor Talent Pool Stalling Your ERP Program?"
        description="Infor is a specialized ERP ecosystem with a significantly smaller certified talent pool than SAP or Oracle. Most staffing agencies lack the Infor-specific knowledge to differentiate between Infor CloudSuite Industrial, LN, and M3 — resulting in placements that look qualified on paper but fail in practice."
        problemTitle="The Real Cost of a Wrong Infor Hire"
        problemItems={problemItems}
        solutionTitle="Kapil Tech: Your Dedicated Infor Staffing Partner"
        solutionDescription="Kapil Tech maintains an Infor-specific staffing practice with recruiters who understand the Infor product landscape — including CloudSuite Automotive, Industrial, Fashion, Food & Beverage, Healthcare, and Distribution. We verify real implementation experience before any candidate profile reaches your team."
        solutionPoints={solutionPoints}
        ctaText="Find Your Infor Talent Today"
      />

      {/* Section 6: Business Outcomes */}
      <TechOutcomes 
        title="Business Outcomes You Can Expect"
        description="Partner with Kapil Tech to accelerate your Infor initiatives and achieve measurable business results through our expert talent solutions."
        outcomes={outcomes}
      />

      {/* Section 7: CTA */}
      <TechCTA 
        title="Ready to Hire Top"
        titleGradient="Infor Experts?"
        description="Share your Infor staffing requirements today. Our team responds within hours with pre-vetted Infor professionals matched to your product, version, and industry."
      />
    </div>
  );
};

export default InforStaffHome;
